import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import { createCategoryUrl } from '~/components/CategoryBlocks/utils';
import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import ChevronRightIcon from '~/components/MarketplaceIcons/ChevronRightIcon';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import { useStores } from '~/hooks/useStore';
import type { Category } from '~/stores/CategoriesStore';

/**
 * What a tile shows of a category; the zone landing passes plain objects
 * with merged counts. A home tile whose preview text holds the numeric id of
 * a catalog category leads to that category instead of itself.
 */
export type CategoryTileItem = Pick<
  Category,
  'id' | 'name' | 'productsCount' | 'previewImage'
> & { previewText?: string };

interface Props {
  items: CategoryTileItem[];
  /** Tile background; a zone landing tints it with the zone colour. */
  tileColor?: string;
  /** Rows of the mobile rail: two on the home, one on a zone landing. */
  mobileRows?: 1 | 2;
  className?: string;
}

const SCROLL_STEP_INSET = 60;
const TILE_IMAGE_WIDTH = 300;

const ARROW_CLASS =
  '-translate-y-1/2 absolute top-1/2 flex size-9 cursor-pointer items-center justify-center rounded-full border border-compact-stroke border-solid bg-white p-0 text-text-primary shadow-[0_2px_10px_rgba(0,0,0,0.08)]';

/**
 * Category tiles of the compact blocks: the children of a home block or of a
 * zone as tiles with the name, the product count and the preview image. A
 * tile pointed at a catalog category takes that category's link, count and,
 * when it has no image of its own, image. The image is drawn at one and a
 * half times the strip height and anchored to its bottom edge, so the object
 * bleeds out of the tile as in the mockup. A centered rail with round arrows
 * on desktop, a horizontal scroll on mobile.
 */
const CompactCategoryTilesView = ({
  items,
  tileColor,
  mobileRows = 2,
  className,
}: Props) => {
  const { isMobile } = useGlobal();
  const { t } = useTranslation();
  const { shopCategories } = useStores();
  const { handleClickCategory } = useCategoryAnalytics();
  const railRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState({ prev: false, next: false });

  const updateOverflow = useCallback(() => {
    const rail = railRef.current;
    if (!rail) {
      return;
    }
    setOverflow({
      prev: rail.scrollLeft > 0,
      next: rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 1,
    });
  }, []);

  const itemsCount = items.length;

  useEffect(() => {
    const rail = railRef.current;
    if (!rail || isMobile) {
      return;
    }
    updateOverflow();
    rail.addEventListener('scroll', updateOverflow, { passive: true });
    window.addEventListener('resize', updateOverflow);

    return () => {
      rail.removeEventListener('scroll', updateOverflow);
      window.removeEventListener('resize', updateOverflow);
    };
  }, [updateOverflow, isMobile, itemsCount]);

  const scrollRail = (direction: 1 | -1) => {
    const rail = railRef.current;
    if (!rail) {
      return;
    }
    rail.scrollBy({
      left: direction * (rail.clientWidth - SCROLL_STEP_INSET),
      behavior: 'smooth',
    });
  };

  const tiles = items
    .filter((item) => item.id)
    .map((item) => {
      const targetId = Number(item.previewText?.trim());
      const target =
        Number.isInteger(targetId) && targetId > 0
          ? shopCategories.locate(targetId)?.node
          : undefined;

      return {
        id: item.id,
        linkId: (target ?? item).id,
        name: item.name,
        productsCount: target?.productsCount ?? item.productsCount,
        previewImage: item.previewImage || target?.previewImage || '',
      };
    });

  if (tiles.length === 0) {
    return null;
  }

  return (
    <section className={cn('relative', isMobile ? 'mt-5' : 'mt-8', className)}>
      <div
        ref={railRef}
        className={cn(
          'scrollbar-hide overflow-x-auto',
          isMobile
            ? '-mx-3.5 grid auto-cols-[128px] grid-flow-col gap-2 px-3.5 pt-0.5 pb-1.5'
            : 'flex gap-3 pt-0.5 pb-1.5 [justify-content:safe_center]',
          isMobile &&
            (mobileRows === 1 || tiles.length < 2
              ? 'grid-rows-1'
              : 'grid-rows-2'),
        )}
      >
        {tiles.map((item) => (
          <Link
            key={item.id}
            to={createCategoryUrl({ id: item.linkId })}
            onClick={handleClickCategory(item.id)}
            data-link={`category-${item.id}`}
            style={tileColor ? { backgroundColor: tileColor } : undefined}
            className={cn(
              'no-underline! flex min-w-0 shrink-0 flex-col items-center overflow-hidden bg-compact-surface text-center transition-[background-color,filter] duration-[120ms] ease-[cubic-bezier(.22,1,.36,1)]',
              tileColor
                ? 'hover:brightness-[0.97]'
                : 'hover:bg-compact-surface-hover',
              isMobile
                ? 'rounded-[12px] px-3 pt-3'
                : 'w-[172px] gap-px rounded-[14px] px-3 pt-2.5',
            )}
          >
            <span
              className={cn(
                'max-w-full truncate font-semibold text-text-primary!',
                isMobile
                  ? 'text-[12px] leading-4'
                  : 'text-[16px] leading-[22px] tracking-[-0.01em]',
              )}
            >
              {item.name}
            </span>
            {item.productsCount > 0 && (
              <span className="text-[12px] text-compact-text-muted leading-4">
                {t('phrases:itemsCount', { count: item.productsCount })}
              </span>
            )}
            <span
              className={cn(
                'relative block w-full overflow-hidden',
                isMobile ? 'mt-1 h-14' : 'mt-0.5 h-24',
              )}
            >
              {item.previewImage && (
                <CloudflareResponseImage
                  src={item.previewImage}
                  srcParams={{ width: TILE_IMAGE_WIDTH }}
                  srcSetParams={defaultSrcSetParams}
                  sizes="172px"
                  alt=""
                  loading="lazy"
                  className="absolute bottom-0 left-1/2 h-[150%] w-auto max-w-none -translate-x-1/2"
                />
              )}
            </span>
          </Link>
        ))}
      </div>
      {!isMobile && overflow.prev && (
        <button
          type="button"
          aria-label={t('back')}
          onClick={() => scrollRail(-1)}
          className={cn(ARROW_CLASS, '-left-[18px]')}
        >
          <ChevronRightIcon className="size-4 rotate-180" />
        </button>
      )}
      {!isMobile && overflow.next && (
        <button
          type="button"
          aria-label={t('next')}
          onClick={() => scrollRail(1)}
          className={cn(ARROW_CLASS, '-right-[18px]')}
        >
          <ChevronRightIcon className="size-4" />
        </button>
      )}
    </section>
  );
};

export const CompactCategoryTiles = observer(CompactCategoryTilesView);
